import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import api from '../services/api';
import { sortSizes } from '../utils/constants';
import LazyImage from '../components/ui/LazyImage';
import { trackProductView, trackAddToCart } from '../utils/analytics';
import { 
  FiHeart, 
  FiShare2, 
  FiTruck, 
  FiRefreshCw, 
  FiShield, 
  FiMinus, 
  FiPlus,
  FiFacebook,
  FiTwitter
} from 'react-icons/fi';

const getProductId = (item) => item?.id || item?._id || null;
const normalizeText = (value) => String(value || '').trim().toLowerCase();

const shuffleArray = (items) => {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const ProductDetail = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [pincode, setPincode] = useState('');
  const [pincodeResult, setPincodeResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addedToCart, setAddedToCart] = useState(false);
  const [error, setError] = useState(null);
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [wishlistMessage, setWishlistMessage] = useState('');
  const [relatedCollectionProducts, setRelatedCollectionProducts] = useState([]);
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await api.publicAPI.getProduct(id);
        
        if (response.data.success) {
          setProduct(response.data.product);
          trackProductView(response.data.product);
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(api.utils.handleError(err));
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  useEffect(() => {
    const fetchRelatedProducts = async () => {
      if (!product) return;

      try {
        setRelatedLoading(true);
        const response = await api.publicAPI.getProducts({ list_only: 'true' });
        if (!response.data?.success) return;

        const allProducts = response.data.products || [];
        const currentId = getProductId(product);
        const currentCollectionId = String(
          product.collection_id || product.collectionId || product.collection?.id || product.collection?._id || ''
        );
        const currentCollectionSlug = normalizeText(product.collection_slug || product.collection?.slug);
        const currentCollectionName = normalizeText(product.collection_name || product.collection?.name);

        const filtered = allProducts.filter((item) => String(getProductId(item) || '') !== String(currentId || ''));

        const sameCollection = filtered.filter((item) => {
          const itemCollectionId = String(
            item.collection_id || item.collectionId || item.collection?.id || item.collection?._id || ''
          );
          const itemCollectionSlug = normalizeText(item.collection_slug || item.collection?.slug);
          const itemCollectionName = normalizeText(item.collection_name || item.collection?.name);

          if (currentCollectionId && itemCollectionId && currentCollectionId === itemCollectionId) return true;
          if (currentCollectionSlug && itemCollectionSlug && currentCollectionSlug === itemCollectionSlug) return true;
          if (currentCollectionName && itemCollectionName && currentCollectionName === itemCollectionName) return true;
          return false;
        });

        const sameCollectionIds = new Set(
          sameCollection.map((item) => String(getProductId(item) || ''))
        );

        const suggestedBase = filtered.filter((item) => {
          const productId = String(getProductId(item) || '');
          return !sameCollectionIds.has(productId);
        });

        const basePool = suggestedBase.length ? suggestedBase : [];
        setRelatedCollectionProducts(sameCollection);
        setSuggestedProducts(shuffleArray(basePool));
      } catch (relatedError) {
        console.error('Error loading related products:', relatedError);
      } finally {
        setRelatedLoading(false);
      }
    };

    fetchRelatedProducts();
  }, [product]);

  const relatedSectionProducts = [...relatedCollectionProducts, ...suggestedProducts];

  const handleAddToCart = () => {
    if (!selectedSize) {
      alert('Please select a size');
      return;
    }
    
    // Check if size is available
    if (!api.utils.isSizeAvailable(product, selectedSize)) {
      alert('Selected size is out of stock');
      return;
    }
    
    // Check if requested quantity is available
    const availableStock = api.utils.getSizeStock(product, selectedSize);
    if (quantity > availableStock) {
      alert(`Only ${availableStock} items available for size ${selectedSize}`);
      return;
    }
    
    addToCart(product, selectedSize, quantity);
    trackAddToCart(product, quantity, selectedSize);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    setQuantity(1); // Reset quantity when size changes
  };

  const handleQuantityChange = (newQuantity) => {
    if (!selectedSize) return;
    
    const availableStock = api.utils.getSizeStock(product, selectedSize);
    const validQuantity = Math.max(1, Math.min(newQuantity, availableStock));
    setQuantity(validQuantity);
  };

  const checkPincode = () => {
    if (pincode.length === 6) {
      // Mock pincode check
      const validPincodes = ['400001', '110001', '560001', '600001', '700001'];
      const isValid = validPincodes.includes(pincode);
      
      setPincodeResult({
        valid: isValid,
        message: isValid 
          ? 'Delivery available in 2-3 business days' 
          : 'Delivery not available in this area',
        cod: isValid
      });
    }
  };

  const handleToggleWishlist = async () => {
    const productId = getProductId(product);
    setWishlistMessage('');

    const result = await toggleWishlist(productId);
    if (result.success) {
      setWishlistMessage(result.added ? 'Added to wishlist' : 'Removed from wishlist');
      window.setTimeout(() => setWishlistMessage(''), 2000);
    } else if (result.error && result.error !== 'login_required') {
      setWishlistMessage(result.error);
      window.setTimeout(() => setWishlistMessage(''), 2500);
    }
  };

  const shareProduct = (platform) => {
    const url = window.location.href;
    const text = `Check out this amazing ${product.name} from BeyondXL!`;
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      instagram: '#' // Instagram doesn't support direct sharing via URL
    };
    
    if (shareUrls[platform] !== '#') {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{error || 'Product not found'}</h2>
          <Link to="/products" className="btn-primary">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const maxQuantity = selectedSize ? api.utils.getSizeStock(product, selectedSize) : 1;
  const isOutOfStock = !api.utils.isProductAvailable(product);
  const isSizeOutOfStock = selectedSize ? !api.utils.isSizeAvailable(product, selectedSize) : false;
  const displayStock = selectedSize
    ? api.utils.getSizeStock(product, selectedSize)
    : product.totalStock;
  const showOnlyOneLeft = !isOutOfStock && displayStock === 1;
  const productId = getProductId(product);
  const savedToWishlist = isInWishlist(productId);
  const productRating = Number(product.rating);
  const productReviewCount = Number(product.reviewCount ?? product.review_count);
  const showProductRating = Number.isFinite(productRating) && productRating > 0
    && Number.isFinite(productReviewCount) && productReviewCount > 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,380px)_1fr] gap-8 lg:gap-10 items-start">
        {/* Image Gallery */}
        <div className="space-y-4 w-full max-w-[380px] mx-auto lg:mx-0">
          <div className="relative aspect-[4/5] overflow-hidden rounded-lg">
            <LazyImage
              src={product.images?.[selectedImage]}
              alt={product.name}
              width={1200}
              height={1500}
              fit="cover"
              eager
            />
          </div>
          {product.images && product.images.length > 1 && (
            <div className="flex flex-nowrap gap-3 sm:gap-4 w-full">
              {product.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`relative flex-1 min-w-0 aspect-[4/5] overflow-hidden rounded-lg border-2 ${
                    selectedImage === index ? 'border-black' : 'border-gray-200'
                  }`}
                >
                  <LazyImage
                    src={image}
                    alt={`${product.name} ${index + 1}`}
                    width={300}
                    height={375}
                    fit="cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
            {product.collection_name && (
              <p className="text-gray-600 mt-1">{product.collection_name}</p>
            )}
            {showProductRating && (
              <div className="flex items-center mt-2">
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={`text-lg ${star <= Math.round(productRating) ? 'text-yellow-400' : 'text-gray-300'}`}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <span className="ml-2 text-sm text-gray-600">
                  ({productReviewCount.toLocaleString()} {productReviewCount === 1 ? 'review' : 'reviews'})
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-3xl font-bold text-black">
              ₹{product.price.toLocaleString()}
            </span>
            {product.original_price && product.original_price > product.price && (
              <>
                <span className="text-xl text-gray-500 line-through">
                  ₹{product.original_price.toLocaleString()}
                </span>
                <span className="bg-red-100 text-red-800 text-sm px-2 py-1 rounded">
                  {Math.round(((product.original_price - product.price) / product.original_price) * 100)}% OFF
                </span>
              </>
            )}
          </div>

          {product.description && (
            <p className="text-gray-600">{product.description}</p>
          )}

          {/* Stock Status */}
          <div className="p-4 rounded-lg bg-gray-50">
            <div className="flex items-center space-x-2">
              {isOutOfStock ? (
                <>
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-red-600 font-medium">Out of Stock</span>
                </>
              ) : showOnlyOneLeft ? (
                <>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-yellow-600 font-medium">Only 1 left</span>
                </>
              ) : (
                <>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-green-600 font-medium">In Stock</span>
                </>
              )}
            </div>
          </div>

          {/* Size Selection */}
          {product.sizes && product.sizes.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Size</h3>
                <button
                  type="button"
                  onClick={() => setShowSizeChart(true)}
                  className="text-sm font-medium text-gray-700 hover:text-black underline underline-offset-2"
                >
                  Size Chart
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {sortSizes(product.sizes).map((size) => {
                  const sizeStock = api.utils.getSizeStock(product, size);
                  const isSizeAvailable = sizeStock > 0;
                  
                  return (
                    <button
                      key={size}
                      onClick={() => isSizeAvailable && handleSizeSelect(size)}
                      disabled={!isSizeAvailable}
                      className={`px-4 py-2 border rounded-lg font-medium relative ${
                        selectedSize === size
                          ? 'border-black bg-black text-white'
                          : isSizeAvailable
                          ? 'border-gray-300 hover:border-gray-400'
                          : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                      title={
                        !isSizeAvailable
                          ? 'Out of stock'
                          : sizeStock === 1
                          ? 'Only 1 left'
                          : 'In stock'
                      }
                    >
                      {size}
                      {!isSizeAvailable && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-full h-0.5 bg-gray-400 rotate-12"></div>
                        </div>
                      )}
                      {isSizeAvailable && sizeStock === 1 && (
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-500 rounded-full"></div>
                      )}
                    </button>
                  );
                })}
              </div>

            </div>
          )}

          {/* Quantity Selection */}
          {selectedSize && !isSizeOutOfStock && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Quantity</h3>
              <div className="flex items-center space-x-4">
                <div className="flex items-center border rounded-lg">
                  <button
                    onClick={() => handleQuantityChange(quantity - 1)}
                    className="p-2 hover:bg-gray-100 disabled:opacity-50"
                    disabled={quantity <= 1}
                  >
                    <FiMinus size={16} />
                  </button>
                  <span className="px-4 py-2 font-medium">{quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(quantity + 1)}
                    className="p-2 hover:bg-gray-100 disabled:opacity-50"
                    disabled={quantity >= maxQuantity}
                  >
                    <FiPlus size={16} />
                  </button>
                </div>
                <span className="text-sm text-gray-600">
                  Max: {maxQuantity}
                </span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-4">
            <button
              onClick={handleAddToCart}
              disabled={isOutOfStock || isSizeOutOfStock || !selectedSize}
              className={`btn-primary flex-1 ${
                addedToCart ? 'bg-green-600 hover:bg-green-700' : ''
              } ${(isOutOfStock || isSizeOutOfStock || !selectedSize) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isOutOfStock ? 'Out of Stock' : 
               isSizeOutOfStock ? 'Size Out of Stock' :
               !selectedSize ? 'Select Size' :
               addedToCart ? 'Added to Cart!' : 'Add to Cart'}
            </button>
            <button
              type="button"
              onClick={handleToggleWishlist}
              className={`btn-secondary ${savedToWishlist ? 'text-red-600' : ''}`}
              aria-label={savedToWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <FiHeart size={20} fill={savedToWishlist ? 'currentColor' : 'none'} />
            </button>
            <div className="relative group">
              <button className="btn-secondary">
                <FiShare2 size={20} />
              </button>
              <div className="absolute top-full right-0 mt-2 bg-white border rounded-lg shadow-lg p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                <button onClick={() => shareProduct('facebook')} className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded whitespace-nowrap">
                  <FiFacebook size={16} />
                  <span>Facebook</span>
                </button>
                <button onClick={() => shareProduct('twitter')} className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded whitespace-nowrap">
                  <FiTwitter size={16} />
                  <span>Twitter</span>
                </button>
              </div>
            </div>
          </div>
          {wishlistMessage && (
            <p className="text-sm text-gray-600">{wishlistMessage}</p>
          )}

          {/* Features */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                <FiTruck size={16} />
                <span className="text-sm">Free Delivery</span>
              </div>
              <div className="flex items-center space-x-2">
                <FiRefreshCw size={16} />
                <span className="text-sm">Easy Returns</span>
              </div>
              <div className="flex items-center space-x-2">
                <FiShield size={16} />
                <span className="text-sm">Quality Assured</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(relatedLoading || relatedSectionProducts.length > 0) && (
        <section className="mt-16">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold">You May Also Like</h2>
            </div>
            <Link to="/products" className="text-sm text-gray-600 hover:text-black">
              View All
            </Link>
          </div>

          {relatedLoading && relatedSectionProducts.length === 0 ? (
            <div className="product-grid">
              {[1, 2, 3, 4].map((item) => (
                <div key={item} className="card animate-pulse">
                  <div className="aspect-[4/5] bg-gray-200" />
                  <div className="p-4">
                    <div className="h-4 bg-gray-200 rounded mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-2/3 mb-3" />
                    <div className="h-5 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="product-grid">
              {relatedSectionProducts.map((item, index) => {
                const itemId = getProductId(item);
                const inSameCollection = index < relatedCollectionProducts.length;

                return (
                  <Link
                    key={itemId || `related-${index}`}
                    to={itemId ? `/products/${itemId}` : '/products'}
                    className="card group block"
                  >
                    <div className="relative overflow-hidden aspect-[4/5] bg-white">
                      <LazyImage
                        src={item.images?.[0]}
                        alt={item.name}
                        width={600}
                        height={750}
                        fit="cover"
                        className="group-hover:scale-105 transition-transform duration-300"
                      />
                      {inSameCollection && (
                        <span className="absolute top-3 left-3 bg-black text-white text-xs px-2 py-1 rounded">
                          Same Collection
                        </span>
                      )}
                    </div>
                    <div className="product-card-info">
                      <h3 className="text-base lg:text-sm font-semibold text-gray-900 line-clamp-2">{item.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{item.collection_name || 'Collection'}</p>
                      <p className="text-base lg:text-sm font-bold mt-2">₹{Number(item.price || 0).toLocaleString('en-IN')}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      )}

      {showSizeChart && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setShowSizeChart(false)}
              className="absolute top-3 right-3 bg-white/90 text-gray-700 hover:text-black border border-gray-200 rounded px-2 py-1 text-sm"
            >
              Close
            </button>
            <img
              src="/BXL%20Size%20chart.jpg"
              alt="Size Chart"
              className="w-full h-auto max-h-[85vh] object-contain"
            />
          </div>
        </div>
      )}

    </div>
  );
};

export default ProductDetail; 